import { useAccount } from '../context/account-context';
import { useMemo } from 'react';
import { roundTo } from '../utils/helpers/round-to';

// Cache for Intl formatters to avoid recreating them
const formattersCache = new Map<string, Intl.NumberFormat>();

/**
 * Creates or retrieves a cached Intl.NumberFormat formatter
 * @param locale - The locale string
 * @param currency - The currency code
 * @returns An Intl.NumberFormat instance
 */
const getFormatter = (locale: string, currency: string): Intl.NumberFormat => {
  const key = `${locale}-${currency}`;

  if (!formattersCache.has(key)) {
    try {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.toUpperCase(),
      });
      formattersCache.set(key, formatter);
    } catch (error) {
      // Fallback to EUR if currency is invalid
      console.warn(`Invalid currency ${currency}, falling back to EUR`);
      const fallbackKey = `${locale}-EUR`;
      if (!formattersCache.has(fallbackKey)) {
        const fallbackFormatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: 'EUR',
        });
        formattersCache.set(fallbackKey, fallbackFormatter);
      }
      return formattersCache.get(fallbackKey)!;
    }
  }

  return formattersCache.get(key)!;
};

/**
 * React hook that provides a currency formatting function with automatic fallback to company currency.
 *
 * This hook leverages the user's account context to automatically use the company's payment currency
 * when no specific currency code is provided. It ensures consistent currency formatting across the
 * application while allowing explicit currency override for specific use cases (e.g., historical orders).
 *
 * Features:
 * - SSR-safe: Handles localStorage access safely on server and client
 * - Performance optimized: Uses cached Intl.NumberFormat instances
 * - Error resilient: Falls back to EUR if invalid currency is provided
 * - Locale-aware: Uses stored language preference or defaults to fr-FR
 *
 * @returns A memoized formatting function that takes a value and optional currency code
 *
 * @example
 * ```tsx
 * const ProductPrice = ({ price }) => {
 *   const formatMoneyAmount = useFormatMoneyAmount();
 *
 *   // Uses company's payment currency as fallback
 *   return <span>{formatMoneyAmount(price)}</span>;
 * };
 * ```
 *
 * @example
 * ```tsx
 * const OrderSummary = ({ order }) => {
 *   const formatMoneyAmount = useFormatMoneyAmount();
 *
 *   // Uses specific order currency (for historical orders)
 *   return <span>{formatMoneyAmount(order.total, order.currencyCode)}</span>;
 * };
 * ```
 */
export const useFormatMoneyAmount = () => {
  const { company } = useAccount();

  /**
   * Formats a monetary amount using Intl.NumberFormat with currency display.
   *
   * @param value - The numeric amount to format
   * @param currencyCode - Optional currency code (ISO 4217). If not provided, uses company's payment currency, then falls back to 'eur'
   * @returns Formatted currency string (e.g., "€123.45") or empty string for invalid values
   */
  return useMemo(() => {
    return (value: number, currencyCode?: string | null | undefined) => {
      if (!isFinite(value) || value === null) return '';

      const rounded = roundTo(value, 6);

      // Avoid SSR issues by checking if we're on the client side
      const lang =
        typeof window !== 'undefined'
          ? localStorage?.getItem('i18nextLngCde') ?? 'fr-FR'
          : 'fr-FR';

      // If no currencyCode is provided, use the company's currency
      const finalCurrencyCode =
        currencyCode ?? company?.paymentCurrencyCode ?? 'eur';

      const formatter = getFormatter(lang, finalCurrencyCode);
      return formatter.format(rounded);
    };
  }, [company?.paymentCurrencyCode]);
};
